import { ChangeDetectionStrategy, Component, linkedSignal, model, OnInit, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { CurrencyCode, simpleDeepClone } from '@phx-erp/shared';
import { distinctUntilChanged, map } from 'rxjs';
import { WidgetBaseDetailComponent } from '../../../../shared/components/widget-base-detail/widget-base-detail.component';
import { FormConfigIdentifier } from '../../../../@core/constants/form-config-identifier';
import { Dialog } from "../../../../@core/services/modal/modal.interfaces";
import { BaseDetailComponent } from '../../../../common/base-detail.component';
import { findFieldByKey } from '../../../../common/utilities/ngx-formly.helper';
import { Address, Operator, TaxType, VendorPriceList, VendorPriceListAddressLink } from '../../../../generated/generated-types';
import { HotkeysService } from '../../../hotkeys/services/hotkeys.service';
import { VendorPriceListAddressLinkService } from '../../services/vendor-price-list-address-link.service';
import { VendorPriceListService } from '../../services/vendor-price-list.service';
import { VendorPriceListDetailFallbackFields } from './fallback';

@Component({
  selector: 'app-vendor-price-list-detail-widget',
  templateUrl: '../../../../common/base-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Default, // Dont change to OnPush, until WidgetBaseDetailComponent is updated to support it.
  standalone: true,
  providers: [HotkeysService],
  imports: [
    WidgetBaseDetailComponent
  ]
})

/** usually showed in popup */
export class AddVendorPriceListDetailWidgetComponent extends BaseDetailComponent<VendorPriceListAddressLink> implements OnInit, Dialog {

  productId = model<string>();
  address = model<Address>();
  addressIdCached: string;

  resolveWith: (result?: VendorPriceListAddressLink) => void;
  vendorPriceListIdSignal = signal<string>(null);

  /**
   * Evaluate the vendor price list id linked signal and hide/show the currency and price includes tax fields accordingly.
   * @description On FallbackFields hide is set to true by default to avoid flickering when the form is built and then updated.
   */
  evaluateVendorPriceListIdLinkedSignal = linkedSignal(() => {
    if (this.vendorPriceListIdSignal() && this.vendorPriceListIdSignal() !== null) {
      const currencyField = findFieldByKey(this.fields, 'vendorPriceList.currency');
      currencyField.expressions.hide = "true";
      const priceIncludesTaxField = findFieldByKey(this.fields, 'vendorPriceList.priceIncludesTax');
      priceIncludesTaxField.expressions.hide = "true";
    }
    else {
      const currencyField = findFieldByKey(this.fields, 'vendorPriceList.currency');
      currencyField.expressions.hide = "false";
      const priceIncludesTaxField = findFieldByKey(this.fields, 'vendorPriceList.priceIncludesTax');
      priceIncludesTaxField.expressions.hide = "false";
    }
  })

  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  private vendorPriceListService = inject(VendorPriceListService);
  private vendorPriceListLinkService = inject(VendorPriceListAddressLinkService);
  protected override crudService = this.vendorPriceListLinkService;

  constructor() {
    super();
    this.formConfigIdentifier = FormConfigIdentifier.VENDOR_PRICE_LIST_ADDRESS_LINK;
    this.destroyRef.onDestroy(() => {
      console.log(`Destroying ${this.constructor.name}...`);
    });
  }

  override ngOnInit(): void {

    this.actionBarLocationId = 'vendor-price-list-group-detail';


    if (this.detailModel.vendorPriceList == null)
      this.detailModel.vendorPriceList = {} as VendorPriceList;
    if (this.detailModel && this.detailModel?.vendorPriceList?.currency)
      this.detailModel.vendorPriceList.currency = this.tenantService.getTenantCurrency() || CurrencyCode.EUR;

    super.ngOnInit();
  }

  override async ngAfterViewInit(): Promise<void> {
    super.ngAfterViewInit();
    this.fallbackFields = VendorPriceListDetailFallbackFields();
  }

  buildForm(): void {
    super.buildForm();

    this.detailForm.controls['address'].valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
      )
      .subscribe(async address => {
        if (address && this.addressIdCached !== address.id) {
          this.addressIdCached = address.id;
          this.setLoading();

          let newModel = simpleDeepClone(this.detailModel);

          let existingSpecificLink: VendorPriceListAddressLink;
          //check if there is already links (doesnt matter which one) and priceList for this address and product to set currency & priceIncludesTax
          existingSpecificLink = (await this.vendorPriceListLinkService.getItemDataByProductAndAddress(this.productId(), address.id));
          this.vendorPriceListIdSignal.set(existingSpecificLink?.vendorPriceList?.id);
          if (existingSpecificLink) {

            newModel = {} as VendorPriceListAddressLink;
            this.baseServices.notificationService.warning(_("products.vendor-pricelist-address-already-linked"));
            this.addressIdCached = null;
            //TODO refactor detailModel logic, needs both here otherwise not works in all cases
            this.detailModelSig.set(newModel);
            this.detailModel = newModel;
            this.detailForm.reset();
            this.cdRef.markForCheck();
            this.setLoading(false);
            return;
          }

          const anyExistingLinksToAddress: VendorPriceListAddressLink[] = (await this.vendorPriceListLinkService.getItems({ take: 1, searchFilter: { field: "addressId", operator: Operator.equal, value: address.id } }).single$.pipe(map(x => x.getVendorPriceListAddressLinks)).toPromise()).items as VendorPriceListAddressLink[]
          let existingVendorPriceList: VendorPriceList = {} as VendorPriceList;
          if (anyExistingLinksToAddress?.length > 0)
            existingVendorPriceList = await this.vendorPriceListService.getItemData(anyExistingLinksToAddress[0].vendorPriceListId ?? anyExistingLinksToAddress[0].vendorPriceList?.id); // not necessary all in link
          this.vendorPriceListIdSignal.set(existingVendorPriceList?.id);
          let priceIncludesTax: boolean;
          //otherwise its a reference and readonly
          newModel.vendorPriceList = simpleDeepClone(existingVendorPriceList);
          if (!newModel.vendorPriceList?.id) {
            priceIncludesTax = address.debitor?.taxType === TaxType.GROSS;
            newModel.vendorPriceList.priceIncludesTax = priceIncludesTax;
            newModel.vendorPriceList.currency = address.debitor?.currency;
          }

          if (!newModel.vendorPriceList.currency)
            newModel.vendorPriceList.currency = this.tenantService.getTenantCurrency();

          this.detailModelSig.set(newModel);
          this.detailModel = newModel;
          this.detailForm.patchValue(newModel, { emitEvent: false });
          this.setLoading(false);
        }
        // Why here? We need to make the active it, only when the address changes, not before, since expressions wont exist yet.
        this.evaluateVendorPriceListIdLinkedSignal();
        this.cdRef.detectChanges();
      });
  }

  async save(detailModel: VendorPriceListAddressLink) {
    detailModel = detailModel ?? this.detailModel;
    detailModel.productId = this.productId();
    if (!detailModel.address)
      detailModel.address = this.address();
    try {

      let saved = await super.save(detailModel);

      if (this.resolveWith)
        this.resolveWith(saved);
      return saved
    }
    catch (e: any) {
      this.setLoading(false);
    }
  }

  setLoading(loading: boolean = true) {
    this.loading.set(loading);
  }
}
